import { useId, type ReactNode } from 'react';
import {
  ROI_CALCULATORS,
  ROI_LABELS,
  type RoiCalculatorId,
  type RoiDisplay,
  type RoiDisplayKey,
  type RoiHoursMode,
  type RoiLabels,
  type RoiNumberInput,
} from '@wr/shared';

/**
 * The simulator's eight sections, as the prototype draws them (docs/prototype.html,
 * viewRoi): inputs in sections 1, 3, 7 and 8, results in the others. Used by
 * the project page and by Show to client. The values come from the engine
 * of packages/shared, computed on the device as the person types.
 */

/** The fields as typed (text), and the hours mode. */
export type RoiFieldValues = Record<RoiNumberInput, string> & { hoursMode: RoiHoursMode };

export interface SimulatorProps {
  calculator: RoiCalculatorId;
  values: RoiFieldValues;
  display: RoiDisplay;
  openSections: number[];
  onToggle(section: number, open: boolean): void;
  onChange(field: RoiNumberInput | 'hoursMode', value: string): void;
}

const CLIENT_INPUTS: RoiNumberInput[] = [
  'annualTonnage',
  'disposalCost',
  'operatingDays',
  'hoursPerDay',
  'sortorsPerShift',
  'pickerCost',
  'totalSorterTonsInput',
];
const ROBOT_INPUTS: RoiNumberInput[] = [
  'capexRobot',
  'numRobots',
  'depreciationPeriod',
  'maintenancePercent',
  'robotPicksPerMin',
];

/** The disclaimer of the simulator, on screen, in Show to client and in the PDF. */
export function Disclaimer({ labels }: { labels: RoiLabels }) {
  return (
    <div className="crd-disc">
      <strong>{labels.disclaimerTitle}</strong> {labels.disclaimer}
    </div>
  );
}

/** The three key figures, live (prototype: .roi-mini). */
export function KeyFigures({
  calculator,
  display,
}: {
  calculator: RoiCalculatorId;
  display: RoiDisplay;
}) {
  const labels = ROI_LABELS[ROI_CALCULATORS[calculator].lang];
  return (
    <div className="roi-mini" aria-label="Key figures">
      <div>
        <b>{display.text.payback}</b>
        <span>{labels.keyFigures.payback}</span>
      </div>
      <div>
        <b>{display.text.roiYear1}</b>
        <span>{labels.keyFigures.roiYear1}</span>
      </div>
      <div>
        <b>{display.text.robotProfit}</b>
        <span>{labels.keyFigures.profit}</span>
      </div>
    </div>
  );
}

function Section({
  n,
  title,
  open,
  onToggle,
  children,
}: {
  n: number;
  title: string;
  open: boolean;
  onToggle(section: number, open: boolean): void;
  children: ReactNode;
}) {
  return (
    <details
      className="crd-sec"
      open={open}
      onToggle={(e) => {
        const now = (e.currentTarget as HTMLDetailsElement).open;
        if (now !== open) onToggle(n, now);
      }}
    >
      <summary>{title}</summary>
      <div className="crd-body">{children}</div>
    </details>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: string;
  onChange(value: string): void;
  min?: number;
  max?: number;
}) {
  return (
    <label className="f crd-in">
      {label}
      <input
        type="number"
        inputMode="decimal"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function Rows({ rows }: { rows: [label: string, value: string, highlight?: boolean][] }) {
  return (
    <div className="crd-out">
      {rows.map(([label, value, hl]) => (
        <div key={label} className={`crd-row${hl ? ' hl' : ''}`}>
          <span>{label}</span>
          <b>{value}</b>
        </div>
      ))}
    </div>
  );
}

type Cell = string | { text: string; win: boolean };
type TableRow = { group: string } | { cells: [string, ...Cell[]]; total?: boolean };

function Table({ head, rows }: { head: ReactNode[]; rows: TableRow[] }) {
  return (
    <div className="scroll-x">
      <table className="yt crd-t">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) =>
            'group' in row ? (
              <tr key={i} className="gh">
                <td colSpan={head.length}>{row.group}</td>
              </tr>
            ) : (
              <tr key={i} className={row.total ? 'tot' : undefined}>
                {row.cells.map((cell, j) =>
                  typeof cell === 'string' ? (
                    <td key={j}>{cell}</td>
                  ) : (
                    <td key={j} className={cell.win ? 'win' : undefined}>
                      {cell.text}
                    </td>
                  ),
                )}
              </tr>
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Simulator({
  calculator,
  values,
  display,
  openSections,
  onToggle,
  onChange,
}: SimulatorProps) {
  const { lang } = ROI_CALCULATORS[calculator];
  const L = ROI_LABELS[lang];
  const t = (key: RoiDisplayKey) => display.text[key];
  const open = (n: number) => openSections.includes(n);
  const modeName = useId();
  const field = (key: RoiNumberInput, range?: { min: number; max: number }) => (
    <NumberField
      key={key}
      label={L.inputs[key]}
      value={values[key]}
      min={range?.min}
      max={range?.max}
      onChange={(v) => onChange(key, v)}
    />
  );
  const w = display.winners;
  const S = L.strategies;
  const E = L.extended;
  const C = L.comparison;

  return (
    <>
      <Section n={1} title={L.sections[0]} open={open(1)} onToggle={onToggle}>
        <div className="crd-grid">{CLIENT_INPUTS.map((k) => field(k))}</div>
      </Section>
      <Section n={2} title={L.sections[1]} open={open(2)} onToggle={onToggle}>
        <Rows
          rows={[
            [L.results.tonsPerSorterDay, t('tonsPerSorterDay')],
            [L.results.totalSorterTonsDisplay, t('totalSorterTonsDisplay')],
            [L.results.sorterRevenue, t('sorterRevenue')],
            [L.results.sorterSalariesTotal, t('sorterSalariesTotal')],
            [L.results.sorterMargin, t('sorterMargin')],
            [L.results.disposalCostTotal, t('disposalCostTotal')],
          ]}
        />
      </Section>
      <Section n={3} title={L.sections[2]} open={open(3)} onToggle={onToggle}>
        <div className="crd-grid">
          {ROBOT_INPUTS.map((k) => field(k))}
          <label className="f crd-in">
            {L.inputs.robotKgPerPick}
            <input
              type="range"
              min="0.3"
              max="3.0"
              step="0.1"
              value={values.robotKgPerPick}
              onChange={(e) => onChange('robotKgPerPick', e.target.value)}
            />
            <b>{t('robotKgDisplay')}</b>
            <small>{t('robotKgHint')}</small>
          </label>
        </div>
      </Section>
      <Section n={4} title={L.sections[3]} open={open(4)} onToggle={onToggle}>
        <Rows
          rows={[
            [L.results.tonsPerRobotDay, t('tonsPerRobotDay')],
            [L.results.totalRobotTons, t('totalRobotTons')],
            [L.results.robotDisposalAvoid, t('robotDisposalAvoid')],
            [L.results.annualOpCost, t('annualOpCost')],
            [L.results.robotProfit, t('robotProfit')],
            [L.results.robotMargin, t('robotMargin')],
          ]}
        />
      </Section>
      <Section n={5} title={L.sections[4]} open={open(5)} onToggle={onToggle}>
        <Rows
          rows={[
            [L.results.payback, t('payback'), true],
            [L.results.roiYear1, t('roiYear1')],
            [L.results.roiYear5, t('roiYear5')],
            [display.roiOverYearsLabel, t('roiOverYears')],
          ]}
        />
      </Section>
      <Section n={6} title={L.sections[5]} open={open(6)} onToggle={onToggle}>
        <Table
          head={[C.metric, C.robots, C.humans]}
          rows={[
            [C.tons, 'compRobotTons', 'compHumanTons', w.tons] as const,
            [C.revenue, 'compRobotRev', 'compHumanRev', w.revenue] as const,
            [C.profit, 'compRobotProfCol', 'compHumanProfCol', w.profit] as const,
            [C.margin, 'compRobotMarginCol', 'compHumanMarginCol', w.margin] as const,
            [C.ratio, 'compRobotRatioCol', 'compHumanRatioCol', w.ratio] as const,
          ].map(([label, robot, human, winner]) => ({
            cells: [
              label,
              { text: t(robot), win: winner === 'robot' },
              { text: t(human), win: winner === 'human' },
            ],
          }))}
        />
      </Section>
      <Section n={7} title={L.sections[6]} open={open(7)} onToggle={onToggle}>
        <div className="crd-grid">{field('sortersReplace', { min: 0, max: 10 })}</div>
        <Table
          head={[
            S.metric,
            <>
              {S.addRobots}
              <br />
              <small>{S.addRobotsHead}</small>
            </>,
            <>
              {S.replaceSorters}
              <br />
              <small>{S.replaceSortersHead}</small>
            </>,
          ]}
          rows={[
            { group: S.configuration },
            { cells: [S.sorters, t('s1Sorters'), t('s2Sorters')] },
            { cells: [S.robots, t('s1RobotsDisplay'), t('s2RobotsDisplay')] },
            { group: S.annualSorting },
            { cells: [S.sortersSorting, t('s1SortersTri'), t('s2SortersTri')] },
            { cells: [S.robotsSorting, t('s1RobotsTri'), t('s2RobotsTri')] },
            { cells: [S.totalSorted, t('s1TotalTri'), t('s2TotalTri')], total: true },
            { cells: [S.vsStatusQuo, t('s1VsStatusPct'), t('s2VsStatusPct')] },
            { group: S.annualCosts },
            { cells: [S.salaries, t('s1Salaries'), t('s2Salaries')] },
            { cells: [S.robotsCost, t('s1RobotsCost'), t('s2RobotsCost')] },
            { cells: [S.totalCost, t('s1TotalCost'), t('s2TotalCost')], total: true },
            { group: S.annualRevenue },
            { cells: [S.total, t('s1Revenue'), t('s2Revenue')] },
            { group: S.profitGroup },
            { cells: [S.annualProfit, t('s1Profit'), t('s2Profit')], total: true },
            { cells: [S.vsStatusQuo, t('s1ProfitVsPct'), t('s2ProfitVsPct')] },
            { cells: [S.salarySavings, '-', t('s2SalarySavings')] },
          ]}
        />
      </Section>
      <Section n={8} title={L.sections[7]} open={open(8)} onToggle={onToggle}>
        <div className="crd-grid">
          {field('s10SortersCount', { min: 0, max: 20 })}
          <div className="f crd-in" role="group" aria-labelledby={`${modeName}-label`}>
            <span id={`${modeName}-label`}>{L.inputs.extraHours}</span>
            <div className="chips">
              {(['weekly', 'total'] as const).map((mode) => (
                <label key={mode} className="chip">
                  <input
                    type="radio"
                    name={modeName}
                    value={mode}
                    checked={values.hoursMode === mode}
                    onChange={() => onChange('hoursMode', mode)}
                  />
                  {L.hoursModes[mode]}
                </label>
              ))}
            </div>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              max="500"
              aria-label={L.inputs.extraHours}
              value={values.extraHours}
              onChange={(e) => onChange('extraHours', e.target.value)}
            />
            <small>{t('hoursInfo')}</small>
          </div>
        </div>
        <Table
          head={[S.metric, E.head]}
          rows={[
            { group: S.configuration },
            { cells: [E.sorters, t('s10SortersDisplay')] },
            { cells: [S.robots, t('s10RobotsDisplay')] },
            { cells: [E.extraHours, t('s10ExtraHoursDisplay')] },
            { group: S.annualSorting },
            { cells: [E.sortersExtra, t('s10SortersExtra')] },
            { cells: [E.robotsExtra, t('s10RobotsExtra')] },
            { cells: [E.totalExtra, t('s10TotalExtra')], total: true },
            { cells: [E.vsAddRobots, t('s10VsStatusPct')] },
            { group: S.annualCosts },
            { cells: [E.salaries, t('s10Salaries')] },
            { cells: [E.robots, t('s10RobotsCost')] },
            { cells: [S.totalCost, t('s10TotalCost')], total: true },
            { group: S.annualRevenue },
            { cells: [S.total, t('s10Revenue')] },
            { group: S.profitGroup },
            { cells: [S.annualProfit, t('s10Profit')], total: true },
            { cells: [E.vsAddRobots, t('s10ProfitVsPct')] },
          ]}
        />
      </Section>
    </>
  );
}
